import { http, type PathParams } from "msw";

import type { SetPasswordRequest, VerifyOtpRequest } from "@/domains/profile/models/types";
import { createOtpVerifiedUser, MOCK_OTP_CODE } from "@/mocks/data/profiling";
import { findPersonaByEmail, PERSONA_ID } from "@/mocks/personas";
import { withLatency } from "@/mocks/resolvers";
import { errorResponse, successResponse } from "@/mocks/responses";
import { createMockToken, MOCK_TOKEN_TTL_SECONDS } from "@/mocks/session";

type ProfilingHandlerOptions = {
  webappUrl: string;
  shopifyOrigin: string;
};

const RETURNING_CUSTOMER_PERSONAS: string[] = [PERSONA_ID.SUBSCRIBED, PERSONA_ID.NOT_SUBSCRIBED];

export const createProfilingHandlers = ({ shopifyOrigin, webappUrl }: ProfilingHandlerOptions) => {
  return [
    http.patch(
      `${webappUrl}/profilings/:responderUuid/password`,
      withLatency<{ responderUuid: string }, Omit<SetPasswordRequest, "variantRevisionUuid">>(
        async ({ request }) => {
          const { password, passwordConfirmation } = await request.json();

          if (password !== passwordConfirmation) {
            return errorResponse({
              status: 422,
              code: "validation_error",
              message: "The given data was invalid.",
              fields: { passwordConfirmation: ["The password confirmation does not match."] },
            });
          }

          return successResponse({
            accessToken: createMockToken(PERSONA_ID.IN_REVIEW),
            expiresIn: MOCK_TOKEN_TTL_SECONDS,
            tokenType: "bearer",
          });
        },
      ),
    ),

    http.get(
      `${webappUrl}/profilings/email-verification`,
      withLatency(({ request }) => {
        const email = new URL(request.url).searchParams.get("email") ?? "";
        const persona = findPersonaByEmail(email);

        return successResponse({
          HP: !!persona && RETURNING_CUSTOMER_PERSONAS.includes(persona.id),
        });
      }),
    ),

    http.post(
      `${webappUrl}/profilings/otp/send`,
      withLatency(() => {
        return successResponse({ message: `We sent a code. Use ${MOCK_OTP_CODE} in this build.` });
      }),
    ),

    http.post(
      `${webappUrl}/profilings/otp/verify`,
      withLatency<PathParams, VerifyOtpRequest>(async ({ request }) => {
        const { code } = await request.json();

        if (code !== MOCK_OTP_CODE) {
          return errorResponse({
            status: 422,
            code: "invalid_otp",
            message: "The code is invalid or has expired.",
            fields: { code: ["The code is invalid or has expired."] },
          });
        }

        return successResponse(createOtpVerifiedUser(shopifyOrigin));
      }),
    ),
  ];
};
